import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import palette from '../../../lib/styles/palette';
import { pointTitle, pointMemo } from '../../../lib/pointLabel';
import {
  loadMemberInfo, loadRunRecord, loadPointHistory, loadPlanProgress, loadRecordDates, loadMemberAttend,
} from '../../../lib/api/runningtrackMonitor';
import {
  ModalHead, ModalBody, Chip, TableWrap, Tbl, Empty, Hint, SmallButton,
} from './ZetaUi';
import {
  fromUnix, dayStart, dayKey, md, ymd, clock, km, dur, pace, num, maskPhone, weekdayLabels,
  getRecordCards,
} from './zetaUtil';

// 회원 상세 — 랭킹·기록한 회원·일별 기록 현황·멀티런·실패 기록 어디서 눌러도 같은 창.
//   제타트랙 기록 = 랭킹·TV 와 같은 일자 기록(runningtrack/record, 최근 30개 날짜) + 기록 & 처방의 기록 카드 사진
//   · 출석 = 출입문 기록 · 포인트 · 플랜 진도.

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid #e0e4ea;
  button {
    border: 0; background: none; padding: 8px 12px; margin-bottom: -1px;
    border-bottom: 2px solid transparent; color: #868e96; font-size: 14px; cursor: pointer; font-family: inherit;
  }
  button[aria-selected='true'] { color: ${palette.webCyan[5]}; border-bottom-color: ${palette.webCyan[5]}; font-weight: 700; }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  > div { border: 1px solid #e0e4ea; border-radius: 10px; padding: 10px 12px; }
  .l { font-size: 12px; color: #868e96; }
  .v { font-size: 20px; font-weight: 700; color: #2c3e50; }
`;

const Thumbs = styled.div`
  display: flex;
  gap: 4px;
  img { width: 48px; height: 36px; object-fit: cover; border-radius: 4px; border: 1px solid #e0e4ea; }
`;

const CalWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
`;
const Cal = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
  width: 340px;
  max-width: 100%;
  .h { font-size: 11px; color: #868e96; text-align: center; }
  .c {
    aspect-ratio: 1; display: grid; place-items: center; border-radius: 4px; font-size: 11px;
    color: #868e96; border: 1px dashed #e0e4ea;
  }
  .c.att { background: #e0e4ea; border-color: transparent; color: #495057; }
  .c.l1 { background: #cfe0fb; border-color: transparent; color: #2c3e50; }
  .c.l2 { background: #97bdf5; border-color: transparent; color: #2c3e50; }
  .c.l3 { background: #5895ee; border-color: transparent; color: #fff; }
  .c.l4 { background: #1A73E9; border-color: transparent; color: #fff; }
  .c.fut { border-style: dotted; opacity: 0.5; }
  .c.today { outline: 2px solid #2c3e50; outline-offset: 1px; }
`;
const Legend = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin-top: 8px; font-size: 11px; color: #868e96;
  i { display: inline-block; width: 12px; height: 12px; border-radius: 3px; }
`;
const MonthNav = styled.div`
  display: flex; align-items: center; gap: 12px;
  strong { min-width: 110px; text-align: center; font-size: 15px; color: #2c3e50; }
`;

const PlanBox = styled.div`
  border: 1px solid #e0e4ea; border-radius: 10px; padding: 14px 16px; display: grid; gap: 8px;
  .top { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
  .track { height: 8px; border-radius: 4px; background: #eef0f4; overflow: hidden; }
  .track i { display: block; height: 100%; background: ${palette.webCyan[5]}; }
`;

const runLevel = (m) => (m < 2000 ? 1 : m < 4000 ? 2 : m < 6000 ? 3 : 4);

// 기록 카드 한 장 — 파일이 없으면(업로드 실패) 깨진 그림 대신 글자로
const CardThumb = ({ url }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return <span style={{ color: '#868e96', fontSize: 12 }}>사진 없음(업로드 안 됨)</span>;
  return <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt="기록 카드" onError={() => setBroken(true)} /></a>;
};

// ── 제타트랙 기록 (랭킹·TV 와 같은 일자 기록, 최근 30개 날짜) ──
const RecordsTab = ({ memberID, socketKey, records, fcDateByDay }) => {
  // 기록 카드는 누를 때만 불러온다 — 날짜마다 기록 & 처방(Koa → Hades)을 부르면 창이 늦게 열린다.
  const [cards, setCards] = useState({});   // 기록 날짜 → 'loading' | 기록 카드 주소[]
  const showCards = async (r) => {
    setCards((prev) => ({ ...prev, [r.date]: 'loading' }));
    const fcDate = fcDateByDay ? fcDateByDay[dayKey(fromUnix(r.date))] : undefined;
    const urls = fcDate ? await getRecordCards(memberID, fcDate, socketKey) : [];
    setCards((prev) => ({ ...prev, [r.date]: urls || [] }));
  };

  if (records === null) return <Empty>불러오는 중…</Empty>;
  if (records === false) return <Empty>제타트랙 기록을 불러오지 못했습니다</Empty>;
  const dist = records.reduce((a, r) => a + r.distance, 0);
  return (
    <>
      <Hint>
        랭킹·TV 앱과 같은 제타트랙 일자 기록입니다(트랩앱 기록은 들어가지 않음). 최근 30개 날짜까지 보이고, 하루 합산이라 1회별 시각은 없습니다.
        시간은 앱이 보낸 값이라 참고값입니다(중간에 멈췄다 다시 뛰면 짧게 잡힘). 기록 카드는 "카드 보기"를 누르면 러닝앱이 올린 사진만 불러옵니다.
      </Hint>
      <Stats>
        <div><div className="l">기록 있는 날 (최근 30개까지)</div><div className="v">{records.length}일</div></div>
        <div><div className="l">1일 평균 거리</div><div className="v">{records.length ? km(Math.round(dist / records.length)) : '-'}</div></div>
        <div><div className="l">마지막 기록</div><div className="v" style={{ fontSize: 16, paddingTop: 4 }}>{records.length ? md(fromUnix(records[0].date)) : '-'}</div></div>
        <div><div className="l">총 거리</div><div className="v">{km(dist)}</div></div>
      </Stats>
      <TableWrap>
        <Tbl>
          <thead><tr><th>날짜</th><th className="r">거리</th><th className="r">시간 (참고)</th><th className="r">평균 페이스</th><th className="r">칼로리</th><th>기록 카드</th></tr></thead>
          <tbody>
            {records.map((r) => {
              const urls = cards[r.date];
              return (
                <tr key={r.date}>
                  <td>{ymd(fromUnix(r.date))} {md(fromUnix(r.date)).replace(/^[^(]*/, '')}</td>
                  <td className="r strong">{km(r.distance)}</td>
                  <td className="r muted">{dur(r.time)}</td>
                  <td className="r muted">{pace(r.distance, r.time)}/km</td>
                  <td className="r">{num(r.calorie)}</td>
                  <td>
                    {urls === undefined ? <SmallButton type="button" onClick={() => showCards(r)} disabled={!fcDateByDay}>카드 보기</SmallButton>
                      : urls === 'loading' ? <span style={{ color: '#868e96' }}>불러오는 중…</span>
                      : urls.length ? <Thumbs>{urls.slice(0, 3).map((u) => <CardThumb key={u} url={u} />)}</Thumbs>
                      : <span style={{ color: '#868e96' }}>없음</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Tbl>
        {!records.length ? <Empty>제타트랙 기록이 없습니다</Empty> : null}
      </TableWrap>
    </>
  );
};

// ── 출석·러닝 달력 (월별 이동) — 러닝은 위와 같은 일자 기록(최근 30개 날짜) ──
const CalendarTab = ({ memberID, clubID, socketKey, records }) => {
  const today = dayStart(new Date());
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [door, setDoor] = useState(null);       // dayKey → 첫 출입 unix

  useEffect(() => {
    let alive = true;
    setDoor(null);
    loadMemberAttend(clubID, memberID, socketKey)
      .then((res) => {
        const map = {};
        (Array.isArray(res.data) ? res.data : []).forEach((x) => {
          const k = dayKey(fromUnix(x.attendTime));
          if (!map[k] || x.attendTime < map[k]) map[k] = x.attendTime;
        });
        if (alive) setDoor(map);
      })
      .catch(() => alive && setDoor({}));
    return () => { alive = false; };
  }, [memberID, clubID, socketKey]);

  const runByDay = {};
  (records || []).forEach((r) => { runByDay[dayKey(fromUnix(r.date))] = r; });
  const oldest = records && records.length ? dayStart(fromUnix(records[records.length - 1].date)) : null;
  const capped = !!(records && records.length >= 30 && oldest && month <= oldest);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const days = [];
  for (let i = 1; i <= monthEnd.getDate(); i++) days.push(new Date(month.getFullYear(), month.getMonth(), i));
  const attDays = door ? days.filter((d) => door[dayKey(d)]).length : 0;
  const runDays = days.filter((d) => runByDay[dayKey(d)]);
  const dist = runDays.reduce((a, d) => a + runByDay[dayKey(d)].distance, 0);
  const lead = (month.getDay() + 6) % 7;
  const rowsDesc = days.slice().reverse().filter((d) => (door && door[dayKey(d)]) || runByDay[dayKey(d)]);

  return (
    <>
      <MonthNav>
        <SmallButton type="button" aria-label="이전 달" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>◀</SmallButton>
        <strong>{month.getFullYear()}년 {month.getMonth() + 1}월</strong>
        <SmallButton type="button" aria-label="다음 달" disabled={month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth()} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>▶</SmallButton>
      </MonthNav>
      {capped ? <Hint style={{ color: '#e8890c' }}>러닝 기록은 최근 30개 날짜({ymd(oldest)} 이후)까지만 볼 수 있어, 이 달의 그 이전 러닝은 표시되지 않습니다. 출석은 전체가 보입니다.</Hint> : null}
      <Stats>
        <div><div className="l">출석일수</div><div className="v">{door ? `${attDays}일` : '…'}</div></div>
        <div><div className="l">러닝 이용일</div><div className="v">{runDays.length}일</div></div>
        <div><div className="l">이용률</div><div className="v">{attDays ? `${Math.min(100, Math.round((runDays.length / attDays) * 100))}%` : '-'}</div></div>
        <div><div className="l">1일 평균 거리</div><div className="v">{runDays.length ? km(Math.round(dist / runDays.length)) : '-'}</div></div>
      </Stats>
      <CalWrap>
        <div>
          <Cal aria-label={`${month.getMonth() + 1}월 출석·러닝 달력`}>
            {weekdayLabels.map((w) => <span key={w} className="h">{w}</span>)}
            {Array.from({ length: lead }, (_, i) => <span key={`b${i}`} />)}
            {days.map((d) => {
              const k = dayKey(d), run = runByDay[k], at = door && door[k];
              const cls = d > today ? ' fut' : run ? ` l${runLevel(run.distance)}` : at ? ' att' : '';
              const tip = `${md(d)}${at ? ` · 출입 ${clock(fromUnix(at))}` : ''}${run ? ` · 러닝 ${km(run.distance)}` : ''}`;
              return <span key={k} className={'c' + cls + (k === dayKey(today) ? ' today' : '')} title={tip}>{d.getDate()}</span>;
            })}
          </Cal>
          <Legend><i style={{ background: '#e0e4ea' }} /> 출석만 &nbsp; <i style={{ background: '#cfe0fb' }} /><i style={{ background: '#97bdf5' }} /><i style={{ background: '#5895ee' }} /><i style={{ background: '#1A73E9' }} /> 러닝 (진할수록 김)</Legend>
        </div>
        <TableWrap style={{ flex: '1 1 380px', minWidth: 0 }}>
          <Tbl>
            <thead><tr><th>날짜</th><th>출입</th><th className="r">러닝 거리</th><th className="r">시간 (참고)</th><th className="r">칼로리</th></tr></thead>
            <tbody>
              {rowsDesc.map((d) => {
                const k = dayKey(d), run = runByDay[k], at = door && door[k];
                return (
                  <tr key={k}>
                    <td>{md(d)}</td>
                    <td>{at ? clock(fromUnix(at)) : <span style={{ color: '#868e96' }}>기록 없음</span>}</td>
                    {run ? (
                      <>
                        <td className="r strong">{km(run.distance)}</td>
                        <td className="r muted">{dur(run.time)}</td>
                        <td className="r">{num(run.calorie)}</td>
                      </>
                    ) : <td className="r muted" colSpan={3}>러닝 안 함</td>}
                  </tr>
                );
              })}
            </tbody>
          </Tbl>
          {!rowsDesc.length ? <Empty>{door ? '이 달에는 출석·러닝 기록이 없습니다' : '불러오는 중…'}</Empty> : null}
        </TableWrap>
      </CalWrap>
    </>
  );
};

// ── 포인트 ──
const PointsTab = ({ memberID, clubID }) => {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const load = async (before) => {
    try {
      const res = await loadPointHistory(memberID, clubID, before || undefined, 30);
      if (!res.data || res.data.result !== 1) { setData(false); return; }
      if (!before) setData(res.data);
      setItems((prev) => (before ? prev.concat(res.data.items) : res.data.items));
      if (before) setData((prev) => ({ ...prev, hasMore: res.data.hasMore, nextBefore: res.data.nextBefore }));
    } catch (e) {
      setData(false);
    }
  };
  useEffect(() => { setItems([]); load(0); }, [memberID, clubID]); // eslint-disable-line react-hooks/exhaustive-deps
  if (data === null) return <Empty>불러오는 중…</Empty>;
  if (data === false) return <Empty>포인트 내역을 불러오지 못했습니다</Empty>;
  return (
    <>
      <Stats>
        <div><div className="l">보유 포인트</div><div className="v">{num(data.balance)}P</div></div>
        <div><div className="l">최근 {data.recentDays}일 적립</div><div className="v" style={{ color: '#2e7d32' }}>+{num(data.recentEarn)}P</div></div>
        <div><div className="l">최근 {data.recentDays}일 사용</div><div className="v" style={{ color: '#e74c3c' }}>-{num(data.recentUse)}P</div></div>
        <div><div className="l">불러온 내역</div><div className="v">{items.length}건</div></div>
      </Stats>
      <TableWrap>
        <Tbl>
          <thead><tr><th>일시</th><th>내용</th><th>메모</th><th className="r">포인트</th></tr></thead>
          <tbody>
            {items.map((it, i) => {
              const row = { action: it.earn ? 1 : 0, fcType: it.fcType, memo: it.memo };
              const t = fromUnix(it.time);
              return (
                <tr key={`${it.time}_${i}`}>
                  <td>{ymd(t)} {clock(t)}</td>
                  <td>{pointTitle(row)}</td>
                  <td className="muted">{pointMemo(row)}</td>
                  <td className="r strong" style={{ color: it.earn ? '#2e7d32' : '#e74c3c' }}>{it.earn ? '+' : '-'}{num(it.point)}P</td>
                </tr>
              );
            })}
          </tbody>
        </Tbl>
        {!items.length ? <Empty>포인트 내역이 없습니다</Empty> : null}
      </TableWrap>
      {data.hasMore ? <div style={{ textAlign: 'center' }}><SmallButton type="button" onClick={() => load(data.nextBefore)}>더 보기</SmallButton></div> : null}
    </>
  );
};

// ── 플랜 ──
const PlanTab = ({ memberID }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    loadPlanProgress(memberID)
      .then((res) => alive && setData(res.data && res.data.result === 1 ? res.data : false))
      .catch(() => alive && setData(false));
    return () => { alive = false; };
  }, [memberID]);
  if (data === null) return <Empty>불러오는 중…</Empty>;
  if (data === false) return <Empty>플랜 진행 정보를 불러오지 못했습니다</Empty>;
  const plans = data.plans || [];
  return (
    <>
      <Hint>최근 7일 완료 <b style={{ color: '#2c3e50' }}>{data.weekSessionCount || 0}회</b> · 권장 주 3회 (두 플랜 합계)</Hint>
      {plans.length ? plans.map((p) => (
        <PlanBox key={p.planID}>
          <div className="top"><strong>{p.planName}</strong>{p.finished ? <Chip tone="blue">완주</Chip> : <Chip tone="green">진행 중</Chip>}</div>
          <div className="track"><i style={{ width: `${Math.round(((p.maxOccurrence || 0) / (p.totalOccurrence || 1)) * 100)}%` }} /></div>
          <Hint>
            {p.maxOccurrence || 0} / {p.totalOccurrence}회차 · 완료 {p.completedCount || 0}번(반복 포함)
            {p.startDate ? ` · 시작 ${ymd(fromUnix(p.startDate))}` : ''}
            {p.lastPlayedAt ? ` · 마지막 ${md(fromUnix(p.lastPlayedAt))}` : ''}
          </Hint>
        </PlanBox>
      )) : <Empty>진행 중인 사다리 플랜이 없습니다</Empty>}
    </>
  );
};

const ZetaMemberModal = ({ open, memberID, clubID, socketKey, onClose }) => {
  const [tab, setTab] = useState('records');
  const [info, setInfo] = useState(null);
  const [bestLapMs, setBestLapMs] = useState(null);
  const [records, setRecords] = useState(null);         // 제타트랙 일자 기록 [{date,distance,time,calorie}] | false
  const [fcDateByDay, setFcDateByDay] = useState(null); // dayKey → 기록 & 처방 날짜 값(기록 카드 찾기용)

  useEffect(() => {
    if (!open || !memberID) return;
    let alive = true;
    setTab('records'); setInfo(null); setBestLapMs(null); setRecords(null); setFcDateByDay(null);
    loadMemberInfo(memberID, clubID)
      .then((res) => alive && setInfo(res.data && res.data.result === 1 ? res.data.info : false))
      .catch(() => alive && setInfo(false));
    loadRunRecord(memberID)
      .then((res) => {
        if (!alive) return;
        const ok = res.data && res.data.result === 1;
        setRecords(ok ? (res.data.infos || []).map((x) => ({
          date: Number(x.date), distance: Number(x.distance) || 0, time: Number(x.time) || 0, calorie: Number(x.calorie) || 0,
        })).sort((a, b) => b.date - a.date) : false);
        setBestLapMs(ok && res.data.best ? res.data.best.bestLapMs : null);
      })
      .catch(() => alive && setRecords(false));
    loadRecordDates(memberID, socketKey)
      .then((res) => {
        const map = {};
        (res.data && Array.isArray(res.data.date) ? res.data.date : []).forEach((d) => { map[dayKey(fromUnix(Number(d)))] = Number(d); });
        if (alive) setFcDateByDay(map);
      })
      .catch(() => alive && setFcDateByDay({}));
    return () => { alive = false; };
  }, [open, memberID, clubID, socketKey]);

  const name = info ? info.name : '';
  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth>
      <ModalHead>
        <div>
          <h3>
            {info === null ? '불러오는 중…' : info === false ? `회원번호 ${memberID}` : name}
            {info && info.nickName ? <span style={{ fontSize: 14, fontWeight: 500, color: '#868e96', marginLeft: 8 }}>{info.nickName}</span> : null}
          </h3>
          <div className="meta">
            {info && info.account ? <span>아이디 <b style={{ color: '#2c3e50' }}>{info.account}</b></span> : null}
            <span>회원번호 <b style={{ color: '#2c3e50' }}>{memberID}</b></span>
            {info && info.phone ? <span>{maskPhone(info.phone)}</span> : null}
            {info ? (info.isNewRunner ? <Chip tone="amber">신규 러너</Chip> : <Chip tone="blue">기존 러너</Chip>) : null}
            {info ? <Chip tone="gray">Lv.{info.level}</Chip> : null}
            {info ? <Chip tone="gray">보유 {num(info.point)}P</Chip> : null}
            {bestLapMs ? <Chip tone="gray">최고 랩 {dur(bestLapMs / 1000)}</Chip> : null}
          </div>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="닫기"><CloseIcon /></IconButton>
      </ModalHead>
      <ModalBody>
        <Tabs role="tablist">
          {[['records', '제타트랙 기록'], ['calendar', '출석·러닝 달력'], ['points', '포인트'], ['plan', '플랜']].map(([k, label]) => (
            <button type="button" role="tab" key={k} aria-selected={tab === k} onClick={() => setTab(k)}>{label}</button>
          ))}
        </Tabs>
        {open && memberID ? (
          <>
            {tab === 'records' && <RecordsTab memberID={memberID} socketKey={socketKey} records={records} fcDateByDay={fcDateByDay} />}
            {tab === 'calendar' && <CalendarTab memberID={memberID} clubID={clubID} socketKey={socketKey} records={records} />}
            {tab === 'points' && <PointsTab memberID={memberID} clubID={clubID} />}
            {tab === 'plan' && <PlanTab memberID={memberID} />}
          </>
        ) : null}
      </ModalBody>
    </Dialog>
  );
};

export default ZetaMemberModal;
